import type { FundSnapshot } from '../../core/types.ts';
import { compact, relTime, solscanTx, usd } from '../lib/format.ts';
import { Empty, Panel, Pill } from './ui.tsx';

export function BurnLog({ funds, now }: { funds: FundSnapshot[]; now: number }) {
  const rows = funds
    .flatMap((f) => f.burns.map((b) => ({ b, f })))
    .sort((a, b) => b.b.ts - a.b.ts)
    .slice(0, 30);

  const totalUsd = funds.reduce((s, f) => s + f.lifetime.burnedUsd, 0);

  return (
    <Panel
      title="Burn ledger"
      subtitle="Every burn is an on-chain buy followed by an SPL burn instruction. Supply only ever goes one way."
      right={<Pill tone="burn">{usd(totalUsd)} destroyed</Pill>}
      bodyClassName=""
    >
      {rows.length === 0 ? (
        <Empty>No burns yet. Realized profits fund buybacks once the minimum burn amount is reached.</Empty>
      ) : (
        <div className="max-h-[320px] overflow-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-panel">
              <tr className="text-[10px] tracking-wide text-dim uppercase">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Coin</th>
                <th className="px-3 py-2 text-right font-medium">Spent</th>
                <th className="px-3 py-2 text-right font-medium">Tokens burned</th>
                <th className="px-3 py-2 text-right font-medium">Avg price</th>
                <th className="px-3 py-2 text-right font-medium">Proof</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ b, f }) => (
                <tr key={b.id} className="border-t border-edge/70">
                  <td className="num px-3 py-2 text-[11px] text-dim">{relTime(b.ts, now)}</td>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-1.5">
                      <img src={f.logo} alt="" className="size-4 rounded-full" width={16} height={16} />
                      <span className="text-[12px] font-medium">${f.symbol}</span>
                    </span>
                  </td>
                  <td className="num px-3 py-2 text-right text-[12px] text-burn">{usd(b.usdSpent)}</td>
                  <td className="num px-3 py-2 text-right text-[12px]">{compact(b.tokensBurned)}</td>
                  <td className="num px-3 py-2 text-right text-[11px] text-dim">
                    ${b.avgPriceUsd < 0.01 ? b.avgPriceUsd.toExponential(2) : b.avgPriceUsd.toFixed(6)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {b.burnTxSig ? (
                      <a
                        href={solscanTx(b.burnTxSig)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-muted underline decoration-dotted hover:text-white"
                      >
                        burn tx ↗
                      </a>
                    ) : (
                      <span className="text-[10px] text-dim" title="Paper mode: no transaction was broadcast">
                        simulated
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
